import React from 'react'
import { Table } from 'react-bootstrap'
import ScreenshotPlaceholder from '../../../components/ScreenshotPlaceholder'
import { Tip, Warn, Code, ArticleLink, Ext, TemplateBtn } from '../_helpers'

export const SECTION_ARTICLES = [
  {
    id: 'data-upload-how',
    category: 'data',
    title: 'Téléverser un fichier, étape par étape',
    summary: 'Comment téléverser un fichier CSV et interpréter le résultat de validation.',
    body: (
      <>
        <p>Une fois votre fichier CSV prêt, téléversez-le depuis l'intérieur de la communauté :</p>
        <ol>
          <li>Ouvrez votre communauté et choisissez <strong>Gestion des Données</strong> dans le menu de gauche.</li>
          <li>Dans <strong>Sélectionner le type de jeu de données</strong>, choisissez le type de fichier que vous téléversez
            (par exemple « Building Load Profile »).</li>
          <li>Si demandé, choisissez à quel <strong>bâtiment</strong> appartient le fichier.</li>
          <li>Faites glisser votre fichier CSV sur la zone de téléversement, ou cliquez sur <strong>Parcourir les fichiers</strong>.</li>
          <li>Cliquez sur <strong>Valider &amp; Aperçu</strong>.</li>
        </ol>
        <ScreenshotPlaceholder
          img="upload-page.png"
          description="The community Data page with a dataset type selected, showing the required-columns badges, the download-template button, and the drag-and-drop upload area."
          source="Platform — open any community → click 'Data Management' in the sidebar → pick a dataset type from the dropdown so the upload area appears. Capture the form area."
          caption="La page Gestion des Données : choisissez un type, puis déposez votre fichier."
        />
        <h5 className="mt-4">Interpréter le résultat</h5>
        <ul>
          <li><strong>Bandeau vert</strong> — le fichier est valide. Il indique le nombre de lignes lues et la plage de dates. Vous avez terminé.</li>
          <li><strong>Avertissements jaunes</strong> — le fichier a été accepté mais quelque chose mérite vérification (par exemple des heures manquantes). Vous pouvez le conserver ou améliorer le fichier.</li>
          <li><strong>Bandeau rouge</strong> — le fichier a été rejeté. L'aperçu liste chaque problème avec son numéro de ligne. Corrigez le fichier et téléversez-le à nouveau.</li>
        </ul>
        <ScreenshotPlaceholder
          img="upload-validation-result.png"
          description="The validation result panel after a successful upload — green success banner plus the data preview table."
          source="Platform — on the same Data Management page, after uploading a valid CSV and clicking 'Validate & Preview'. Capture the green banner and the first few rows of the preview table."
          caption="Un téléversement réussi affiche un bandeau vert et un aperçu."
        />
        <p>
          Chaque message d'erreur rouge est expliqué dans
          <em> « Pourquoi mon téléversement a-t-il échoué ? »</em> dans la section Dépannage.
        </p>
      </>
    ),
  },

  {
    id: 'data-load',
    category: 'data',
    title: 'Fichiers de consommation (charge) des bâtiments',
    summary: 'Le fichier d\'utilisation horaire de l\'électricité — le fichier le plus important pour chaque bâtiment.',
    body: (
      <>
        <p>
          Un <strong>Building Load Profile</strong> enregistre la quantité d'électricité consommée par un bâtiment,
          une valeur par heure. C'est la base pour la prévision et l'optimisation, alors préparez-en
          un pour chaque bâtiment si possible.
        </p>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Colonne</th><th>Obligatoire ?</th><th>Signification</th></tr>
          </thead>
          <tbody>
            <tr><td><Code>timestamp</Code></td><td>Obligatoire</td><td>L'heure, ex. <Code>2025-01-01T08:00:00Z</Code></td></tr>
            <tr><td><Code>consumption_kwh</Code></td><td>Obligatoire</td><td>Électricité consommée cette heure-là, en kWh (un nombre, décimale avec point, jamais négatif)</td></tr>
            <tr><td><Code>peak_demand_kw</Code></td><td>Optionnel</td><td>Puissance maximale appelée durant cette heure, en kW</td></tr>
          </tbody>
        </Table>
        <p><strong>Exemple :</strong></p>
        <pre style={{ background: 'var(--color-neutral-50)', padding: 12, borderRadius: 6, fontSize: '0.8rem' }}>
{`timestamp,consumption_kwh
2025-01-01T00:00:00Z,0.457
2025-01-01T01:00:00Z,0.343
2025-01-01T02:00:00Z,0.298`}
        </pre>
        <p>Une valeur par heure ; une année complète représente 8 760 lignes. Il faut au minimum 24 lignes (une journée).</p>
        <TemplateBtn type="building_load_profile">Télécharger le modèle de charge</TemplateBtn>
        <Warn>La consommation ne peut jamais être négative. Une valeur négative signifie qu'une colonne a été inversée — vérifiez que la production n'a pas été placée dans la colonne de consommation.</Warn>
      </>
    ),
  },

  {
    id: 'data-generation',
    category: 'data',
    title: 'Fichiers de production solaire et éolienne',
    summary: 'Production horaire des panneaux solaires et des éoliennes.',
    body: (
      <>
        <p>Les fichiers de production enregistrent la quantité d'énergie produite par une source renouvelable, une valeur par heure.</p>
        <h5 className="mt-3">Centrale solaire ou éolienne communautaire</h5>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Colonne</th><th>Signification</th></tr>
          </thead>
          <tbody>
            <tr><td><Code>timestamp</Code></td><td>L'heure, ex. <Code>2025-01-01T12:00:00Z</Code></td></tr>
            <tr><td><Code>generation_kw</Code></td><td>Puissance produite cette heure-là, en kW (jamais négatif — la nuit le solaire vaut <Code>0</Code>)</td></tr>
          </tbody>
        </Table>
        <pre style={{ background: 'var(--color-neutral-50)', padding: 12, borderRadius: 6, fontSize: '0.8rem' }}>
{`timestamp,generation_kw
2025-01-01T00:00:00Z,0
2025-01-01T12:00:00Z,105.6
2025-01-01T18:00:00Z,12.3`}
        </pre>
        <TemplateBtn type="pv_generation_profile">Modèle solaire (PV)</TemplateBtn>
        <TemplateBtn type="wind_generation_profile">Modèle éolien</TemplateBtn>

        <h5 className="mt-4">PV en toiture d'un bâtiment</h5>
        <p>
          Pour le solaire appartenant à un seul bâtiment, la colonne de valeur est
          <Code>generation_kwh</Code> (énergie en kWh) au lieu de <Code>generation_kw</Code>.
          La plateforme affiche le nom de colonne exact attendu lorsque vous sélectionnez le type de jeu de données sur la
          page de téléversement — suivez toujours ce qui y est indiqué.
        </p>
        <Tip>Les heures sans soleil ou sans vent doivent contenir <Code>0</Code>, et non rester vides.</Tip>
      </>
    ),
  },

  {
    id: 'data-tariff',
    category: 'data',
    title: 'Fichiers de tarifs (prix de l\'électricité)',
    summary: 'Prix d\'importation et d\'exportation de l\'électricité — historiques et Day-Ahead.',
    body: (
      <>
        <p>Les fichiers de tarifs indiquent à la plateforme le prix de l'électricité pour chaque heure.</p>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Fichier</th><th>Colonne de valeur</th><th>Lignes</th></tr>
          </thead>
          <tbody>
            <tr><td>Tarif d'importation historique</td><td><Code>import_price_eur_kwh</Code></td><td>Un an, horaire (≈8 760)</td></tr>
            <tr><td>Tarif d'exportation historique</td><td><Code>export_price_eur_kwh</Code></td><td>Un an, horaire (≈8 760)</td></tr>
            <tr><td>Tarif d'importation Day-Ahead</td><td><Code>import_price_eur_kwh</Code></td><td>24 (un jour)</td></tr>
            <tr><td>Tarif d'exportation Day-Ahead</td><td><Code>export_price_eur_kwh</Code></td><td>24 (un jour)</td></tr>
          </tbody>
        </Table>
        <p>
          Le prix d'<strong>importation</strong> est ce que vous payez pour acheter de l'électricité ; le prix d'<strong>exportation</strong>
          est ce que vous recevez pour la vendre. Les prix sont en euros par kWh (ex. <Code>0.22</Code>)
          et utilisent un point comme séparateur décimal.
        </p>
        <pre style={{ background: 'var(--color-neutral-50)', padding: 12, borderRadius: 6, fontSize: '0.8rem' }}>
{`timestamp,import_price_eur_kwh
2025-01-01T00:00:00Z,0.08
2025-01-01T12:00:00Z,0.22
2025-01-01T19:00:00Z,0.28`}
        </pre>
        <TemplateBtn type="energy_tariff_data">Modèle tarif d'importation</TemplateBtn>
        <TemplateBtn type="feed_in_tariff_data">Modèle tarif d'exportation</TemplateBtn>
        <Tip>
          Les fichiers <strong>Day-ahead</strong> couvrent uniquement un jour (24 lignes). Téléversez-en un nouveau chaque jour —
          ses horodatages doivent correspondre à la date de ce jour. Les modèles ci-dessus servent également de référence
          pour le format à 24 lignes.
        </Tip>

        <h5 className="mt-4">Où trouver les données tarifaires ?</h5>
        <p>
          Deux choses différentes sont appelées « tarif » — traitez-les séparément :
        </p>
        <ul>
          <li><strong>Prix de gros / Day-ahead</strong> — le prix horaire de marché publié par
            la bourse d'électricité de votre pays. Utilisez-le pour les fichiers de tarif <em>Day-ahead</em> et comme
            bonne approximation des contrats à prix variable.</li>
          <li><strong>Tarif de détail</strong> — ce que vous payez réellement (énergie + frais de réseau + taxes).
            Récupérez-le dans le contrat ou la facture de votre fournisseur.</li>
        </ul>

        <p className="mt-3"><strong>Source paneuropéenne (valable pour tous les pays) :</strong></p>
        <ul>
          <li><strong>ENTSO-E Transparency Platform</strong> —
            {' '}<Ext href="https://transparency.entsoe.eu">transparency.entsoe.eu</Ext>. Gratuit, mais
            nécessite un compte gratuit. Ouvrez <em>Market → Day-ahead Prices</em>, choisissez votre zone d'enchères
            (ex. IT-North, FR, DE-LU, CZ, GR), la plage de dates, puis <em>Export Data</em> → CSV.
            La colonne de prix est en <strong>€/MWh — divisez par 1000 pour convertir en €/kWh</strong>
            (ex. 220 €/MWh = 0,22 €/kWh).</li>
        </ul>

        <p className="mt-3"><strong>Sources nationales (souvent plus simples, sans compte nécessaire) :</strong></p>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Pays</th><th>Site</th><th>Ce que vous obtenez</th></tr>
          </thead>
          <tbody>
            <tr><td>Italie</td><td><Ext href="https://www.mercatoelettrico.org">mercatoelettrico.org</Ext> (GME)</td><td>Prix Day-ahead PUN, horaire. Cherchez « Esiti MGP ». €/MWh — divisez par 1000.</td></tr>
            <tr><td>France</td><td><Ext href="https://data.rte-france.com">data.rte-france.com</Ext> (RTE Open Data) ou <Ext href="https://www.epexspot.com/en">epexspot.com</Ext></td><td>RTE : portail open data gratuit, prix Day-ahead horaires. EPEX : site de marché.</td></tr>
            <tr><td>Allemagne</td><td><Ext href="https://www.smard.de/en">smard.de</Ext> (Agence fédérale des réseaux)</td><td>« Market data » → « Wholesale prices » → « Day-ahead, Germany/Luxembourg ». Téléchargement gratuit, sans compte.</td></tr>
            <tr><td>Rép. tchèque</td><td><Ext href="https://www.ote-cr.cz/en">ote-cr.cz</Ext> (OTE)</td><td>« Short-term markets » → « Day-ahead market ». Export CSV par jour.</td></tr>
            <tr><td>Grèce</td><td><Ext href="https://www.enexgroup.gr/web/guest/markets-publications-el-day-ahead-market">enexgroup.gr</Ext> (HEnEx)</td><td>Résultats du marché Day-Ahead, PDF et CSV quotidiens. €/MWh — divisez par 1000.</td></tr>
          </tbody>
        </Table>

        <h5 className="mt-4">Convertir un fichier de prix téléchargé au format de la plateforme</h5>
        <p>
          Les exports des bourses d'électricité ne correspondent que rarement aux noms de colonnes exacts attendus par la plateforme. Suivez ces
          étapes pour convertir n'importe lequel des fichiers ci-dessus :
        </p>
        <ol>
          <li>Ouvrez le CSV téléchargé dans Excel ou Google Sheets.</li>
          <li>Identifiez la colonne date/heure et la colonne prix (les autres colonnes peuvent être supprimées).</li>
          <li>Créez une colonne appelée <Code>timestamp</Code>. Si la source dispose déjà d'un horodatage ISO,
            copiez-le ; sinon construisez-en un avec la formule indiquée dans
            {' '}<ArticleLink to="data-csv-basics">Créer des fichiers CSV correctement</ArticleLink>.</li>
          <li>Créez une colonne appelée <Code>import_price_eur_kwh</Code>. Si la source est en €/MWh, divisez
            par 1000 avec une formule telle que <Code>=B2/1000</Code> et collez en valeurs uniquement.</li>
          <li>Enregistrez au format <strong>CSV UTF-8</strong> et téléversez comme <em>Tarif d'importation historique</em>.</li>
        </ol>

        <Warn>
          Prix de gros ≠ prix de détail. Le prix de gros correspond uniquement au prix du marché de l'énergie et
          n'inclut pas les frais de réseau, les taxes ou la TVA. Pour une analyse des coûts précise, préférez un tarif de détail
          fourni par votre fournisseur, ou combinez le prix de gros avec un montant fixe additionnel connu.
        </Warn>

        <h5 className="mt-4">Tarif d'exportation (injection)</h5>
        <p>
          Les prix d'exportation dépendent du dispositif national — il peut s'agir d'un tarif d'injection fixe, du
          prix de gros diminué d'une décote, ou de zéro. Obtenez la règle auprès de votre fournisseur, de votre DSO ou
          de votre régulateur national (ARERA en Italie, CRE en France, BNetzA en Allemagne, ERÚ en Tchéquie,
          RAE en Grèce) et saisissez la valeur €/kWh correspondante pour chaque heure.
        </p>
      </>
    ),
  },
]
